// src/AppointmentsByCreationUser.js
import React, { useState } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const AppointmentsByCreationUser = () => {
  // Get the first day of the current month and today's date for default values
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  };
  
  const getToday = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [groupBy, setGroupBy] = useState("createdBy");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Grouping options for the dropdown
  const groupingOptions = [
    { value: "createdBy", label: "Created By" },
    { value: "serviceArea", label: "Service Area" },
    { value: "county", label: "County" },
    { value: "zipCode", label: "Zip Code" },
  ];

  // Get the report title based on selected grouping
  const getReportTitle = () => {
    const selectedOption = groupingOptions.find(option => option.value === groupBy);
    return `Appointments by ${selectedOption?.label || "Creation User"}`;
  };

  // Get the label for the first column based on selected grouping
  const getGroupColumnLabel = () => {
    return groupingOptions.find(option => option.value === groupBy)?.label || "Created By";
  };

  const handleRunReport = () => {
    if (!startDate || !endDate) {
      setError("Please select both a start and end date.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    // Call the API endpoint with the grouping parameter
    apiFetch(`/api/reports/appointments-by-creation-user?start_date=${startDate}&end_date=${endDate}&group_by=${groupBy}`)
      .then((res) => {
        if (!res.ok) {
          return res.text().then(text => { 
            throw new Error("Failed to fetch report data: " + text);
          });
        }
        return res.json();
      })
      .then((data) => {
        setReportData(data);
        setReportGeneratedAt(new Date());
        setLoading(false);
        
        // Set the initial sort column to the first column (groupBy field)
        if (data && data.columns && data.columns.length > 0) {
          setSortColumn(data.columns[0]);
        }
      })
      .catch((err) => {
        console.error("Error running report:", err);
        setError("Error running report: " + err.message);
        setLoading(false);
      });
  };

  // Handle column sorting
  const handleSort = (column) => {
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new column, set it as the sort column with asc direction
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Get sorted data based on current sort column and direction
  const getSortedData = () => {
    if (!reportData || !reportData.rows || !sortColumn) return reportData?.rows || [];
    
    const groupColumn = reportData.columns[0];
    
    // Create a copy of the data for sorting, excluding grand total
    const dataToSort = [...reportData.rows.filter(row => row[groupColumn] !== "Grand Total")];
    const grandTotalRow = reportData.rows.find(row => row[groupColumn] === "Grand Total");
    
    // Sort the data
    dataToSort.sort((a, b) => {
      let valueA, valueB;
      
      if (sortColumn === groupColumn) {
        // For the grouping column (text values)
        valueA = String(a[groupColumn] || "").toLowerCase();
        valueB = String(b[groupColumn] || "").toLowerCase();
        return sortDirection === "asc" 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        // For date columns and totals (numeric values)
        valueA = Number(a[sortColumn]) || 0;
        valueB = Number(b[sortColumn]) || 0;
        return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
      }
    });
    
    // Add grand total row back at the end if it exists
    return grandTotalRow ? [...dataToSort, grandTotalRow] : dataToSort;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "Total") return dateStr;
    
    try {
      // Parse the date parts explicitly to avoid timezone issues
      const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
      const localDate = new Date(year, month - 1, day); // month is 0-indexed in JS Date
      
      // Format as MM/DD/YYYY
      return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  // Format numeric values
  const formatNumber = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {getReportTitle()}
      </Typography>
      
      {/* Report criteria form */}
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400, mb: 2 }}
      >
        <TextField
          label="Start Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          label="End Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        
        {/* Group By dropdown */}
        <FormControl fullWidth>
          <InputLabel id="group-by-label">Group By</InputLabel>
          <Select
            labelId="group-by-label"
            id="group-by-select"
            value={groupBy}
            label="Group By"
            onChange={(e) => setGroupBy(e.target.value)}
          >
            {groupingOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button 
          variant="contained" 
          onClick={handleRunReport}
          disabled={loading}
        >
          {loading ? "Loading..." : "Run Report"}
        </Button>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Report header with title and generation timestamp */}
      {reportGeneratedAt && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">{getReportTitle()}</Typography>
          <Typography variant="caption">
            Report period: {startDate} to {endDate} | Generated: {reportGeneratedAt.toLocaleString()}
            <br />
            Click on column headers to dynamically sort the columns.
          </Typography>
        </Box>
      )}
      
      {/* Report results display */}
      {reportData && reportData.rows && reportData.rows.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <TableContainer component={Paper} sx={{ 
            mb: 4, 
            overflowX: 'auto',
            overflowY: 'auto',
            maxWidth: '100%',
            maxHeight: '600px', // Add max height to enable vertical scrolling
            position: 'relative', // Required for sticky positioning to work
            '&::-webkit-scrollbar': {
              height: '8px',
              width: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#bdbdbd',
              borderRadius: '4px'
            }
          }}>
            <Table stickyHeader size="small" sx={{ 
              '& .MuiTableCell-root': { 
                fontSize: '0.75rem', 
                padding: '6px 16px',
                whiteSpace: 'nowrap',
                minWidth: '55px' // Ensure cells have minimum width
              }
            }}>
              <TableHead>
                <TableRow sx={{ fontWeight: 'bold', '& th': { fontWeight: 'bold' } }}>
                  {/* First Column - Group By Field */}
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      top: 0,
                      zIndex: 4, // Highest z-index for corner cell
                      backgroundColor: 'white',
                      minWidth: '120px', // Wider first column for longer text
                      borderRight: '1px solid #e0e0e0' // Visual separator
                    }}
                    onClick={() => reportData.columns[0] && handleSort(reportData.columns[0])}
                  >
                    {getGroupColumnLabel()}
                    {sortColumn === reportData.columns[0] && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  
                  {/* Date Columns */}
                  {reportData.columns
                    .filter(col => col !== reportData.columns[0] && col !== "Total")
                    .map((dateCol) => (
                      <TableCell 
                        key={dateCol}
                        align="center"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          fontWeight: 'bold',
                          backgroundColor: 'white' // Add background color
                        }}
                        onClick={() => handleSort(dateCol)}
                      >
                        {formatDate(dateCol)}
                        {sortColumn === dateCol && (
                          sortDirection === "asc" ? 
                          <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                          <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                    ))
                  }
                  
                  {/* Total Column */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold',
                      backgroundColor: 'white' // Add background color for consistency
                    }}
                    onClick={() => handleSort("Total")}
                  >
                    Total
                    {sortColumn === "Total" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  
                  {/* Average Count Column */}
                  <TableCell 
                    align="center"
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold',
                      backgroundColor: 'white'
                    }}
                    onClick={() => handleSort("AvgCount")}
                  >
                    Avg Count
                    {sortColumn === "AvgCount" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSortedData().map((row, idx) => {
                  const groupCol = reportData.columns[0];
                  const isGrandTotal = row[groupCol] === "Grand Total";
                  
                  return (
                    <TableRow 
                      key={idx} 
                      sx={{ 
                        backgroundColor: isGrandTotal ? '#f5f5f5' : 'inherit',
                        fontWeight: isGrandTotal ? 'bold' : 'normal',
                        '& td': { 
                          fontWeight: isGrandTotal ? 'bold' : 'normal'
                        }
                      }}
                    >
                      {/* Group By Field Cell */}
                      <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                          fontWeight: isGrandTotal ? 'bold' : 'normal',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          backgroundColor: isGrandTotal ? '#f5f5f5' : 'white'
                        }}
                      >
                        {row[groupCol]}
                      </TableCell>
                      
                      {/* Data cells for each date column */}
                      {reportData.columns
                        .filter(col => col !== groupCol && col !== "Total")
                        .map((dateCol) => (
                          <TableCell 
                            key={dateCol} 
                            align="center"
                            sx={{ fontWeight: isGrandTotal ? 'bold' : 'normal' }}
                          >
                            {formatNumber(row[dateCol])}
                          </TableCell>
                        ))
                      }
                      
                      {/* Total Cell */}
                      <TableCell 
                        align="center"
                        sx={{ fontWeight: isGrandTotal ? 'bold' : 'normal' }}
                      >
                        {formatNumber(row.Total)}
                      </TableCell>
                      
                      {/* Average Count Cell */}
                      <TableCell 
                        align="center"
                        sx={{ fontWeight: isGrandTotal ? 'bold' : 'normal' }}
                      >
                        {(() => {
                          // Get all date columns (excluding the group column and Total)
                          const dateCols = reportData.columns.filter(col => 
                            col !== groupCol && col !== "Total"
                          );
                          
                          // Count non-null values and calculate sum
                          let sum = 0;
                          let count = 0;
                          
                          dateCols.forEach(col => {
                            if (row[col] !== null && row[col] !== undefined) {
                              sum += Number(row[col]);
                              count++;
                            }
                          });
                          
                          // Calculate average (or return 0 if no values)
                          const avg = count > 0 ? sum / count : 0;
                          
                          // Set the average on the row object for sorting
                          row.AvgCount = avg;
                          
                          return formatNumber(avg);
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </Box>
      ) : reportGeneratedAt && (
        <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
          No appointment data found for the selected date range.
        </Typography>
      )}
    </Box>
  );
};

export default AppointmentsByCreationUser;